import { createApi } from '@reduxjs/toolkit/query/react'
import { hcPortalBaseQuery } from '@/features/baseQuery.ts'
import {
  OvertimeRequest,
  OvertimeRequestDecryptedData,
  OvertimeRequestDetails
} from '@/features/overtime/overtime.def.ts'
import {
  AdvanceOvertimeApprovalStepRequest,
  CancelOvertimeRequestRequest,
  ConfirmOvertimePayInputRequest,
  CreateOvertimeRequestRequest,
  FinalizeOvertimeRequestRequest,
  GetOvertimePayDefaultRequest,
  GetOvertimeRequestByIdRequest,
  GetOvertimeRequestsRequest,
  OvertimePayDefault,
  ReplyOvertimeApprovalRequest,
  SubmitOvertimePayInputRequest,
} from '@/features/overtime/overtime-request.def.ts'

export const overtimeApi = createApi({
  reducerPath: 'overtimeApi',
  baseQuery: hcPortalBaseQuery(),
  tagTypes: ['OvertimeRequest'],
  endpoints: (builder) => ({
    getOvertimeRequests: builder.query<OvertimeRequestDetails<OvertimeRequest>[], GetOvertimeRequestsRequest>({
      query: (request) => ({
        url: 'Overtime/GetOvertimeRequests',
        method: 'GET',
        params: request,
      }),
      providesTags: () => [{ type: 'OvertimeRequest', id: 'LIST' }],
    }),
    getOvertimeRequestById: builder.query<OvertimeRequestDetails<OvertimeRequestDecryptedData>, GetOvertimeRequestByIdRequest>({
      query: (request) => ({
        url: 'Overtime/GetOvertimeRequestById',
        params: request,
      }),
      providesTags: (_, __, { id }) => [{ type: 'OvertimeRequest', id }],
    }),
    getOvertimePayDefault: builder.query<OvertimePayDefault, GetOvertimePayDefaultRequest>({
      query: (request) => ({
        url: 'Overtime/GetOvertimePayDefault',
        params: request,
      }),
    }),
    createOvertimeRequest: builder.mutation<OvertimeRequest, CreateOvertimeRequestRequest>({
      query: (request) => ({
        url: 'Overtime/CreateOvertimeRequest',
        method: 'POST',
        body: request,
      }),
      invalidatesTags: [{ type: 'OvertimeRequest', id: 'LIST' }],
    }),
    submitOvertimePayInput: builder.mutation<boolean, SubmitOvertimePayInputRequest>({
      query: (request) => ({
        url: 'Overtime/SubmitOvertimePayInput',
        method: 'POST',
        body: request,
      }),
      invalidatesTags: (_, __, { id }) => [{ type: 'OvertimeRequest', id }, { type: 'OvertimeRequest', id: 'LIST' }],
    }),
    confirmOvertimePayInput: builder.mutation<boolean, ConfirmOvertimePayInputRequest>({
      query: (request) => ({
        url: 'Overtime/ConfirmOvertimePayInput',
        method: 'POST',
        body: request,
      }),
      invalidatesTags: (_, __, { id }) => [{ type: 'OvertimeRequest', id }, { type: 'OvertimeRequest', id: 'LIST' }],
    }),
    replyOvertimeApproval: builder.mutation<boolean, ReplyOvertimeApprovalRequest>({
      query: (request) => ({
        url: 'Overtime/ReplyOvertimeApproval',
        method: 'POST',
        body: request,
      }),
      invalidatesTags: (_, __, { id }) => [{ type: 'OvertimeRequest', id }, { type: 'OvertimeRequest', id: 'LIST' }],
    }),
    advanceOvertimeApprovalStep: builder.mutation<boolean, AdvanceOvertimeApprovalStepRequest>({
      query: (request) => ({
        url: 'Overtime/AdvanceOvertimeApprovalStep',
        method: 'POST',
        body: request,
      }),
      invalidatesTags: (_, __, { id }) => [{ type: 'OvertimeRequest', id }, { type: 'OvertimeRequest', id: 'LIST' }],
    }),
    finalizeOvertimeRequest: builder.mutation<boolean, FinalizeOvertimeRequestRequest>({
      query: (request) => ({
        url: 'Overtime/FinalizeOvertimeRequest',
        method: 'POST',
        body: request,
      }),
      invalidatesTags: (_, __, { id }) => [{ type: 'OvertimeRequest', id }, { type: 'OvertimeRequest', id: 'LIST' }],
    }),
    cancelOvertimeRequest: builder.mutation<boolean, CancelOvertimeRequestRequest>({
      query: (request) => ({
        url: 'Overtime/CancelOvertimeRequest',
        method: 'POST',
        body: request,
      }),
      invalidatesTags: (_, __, { id }) => [{ type: 'OvertimeRequest', id }, { type: 'OvertimeRequest', id: 'LIST' }],
    }),
  }),
})

export const {
  useGetOvertimeRequestsQuery,
  useGetOvertimeRequestByIdQuery,
  useGetOvertimePayDefaultQuery,
  useCreateOvertimeRequestMutation,
  useSubmitOvertimePayInputMutation,
  useConfirmOvertimePayInputMutation,
  useReplyOvertimeApprovalMutation,
  useAdvanceOvertimeApprovalStepMutation,
  useFinalizeOvertimeRequestMutation,
  useCancelOvertimeRequestMutation,
} = overtimeApi
